import React, { useState, useEffect, useCallback, useRef, useLayoutEffect, useMemo } from 'react';
import { incluyeTexto, normalizarTexto } from '../utils/texto';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Plus, ArrowLeft, Send, Paperclip, Download, X,
    Monitor, Cpu, Wifi, Shield, Mail,
    Smartphone, HardDrive, Printer, Database, FolderOpen,
    Phone, Fingerprint, Globe,
    CircleDot, CircleCheck, Circle, ChevronRight,
    UserCheck, MessageSquare, Search, RotateCcw,
    Clock, Wand2, BookOpen, Zap, BarChart3,
    AlertTriangle, AlertCircle, ArrowUp, ArrowDown, Minus,
    Trash2, Save, Loader2, ChevronDown,
    Users, UserPlus, UserMinus, Eye, Snowflake, Play, Link2, Check, KanbanSquare, Maximize2,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../utils/axiosConfig';
import FileViewer from './FileViewer';
import Modal from './common/Modal';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import CrearTareaModal from './Proyectos/CrearTareaModal';
import TareasRelacionadasPanel from './Proyectos/TareasRelacionadasPanel';
import TicketsReportes from './TicketsReportes';
import './TicketsSoporte.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const BASE_URL = API_URL.replace(/\/api\/?$/, '');

// ============================================================
// CONSTANTES
// ============================================================

const CATEGORIAS = [
    { key: 'acceso_remoto', label: 'Acceso Remoto', icon: Globe, color: '#6366f1' },
    { key: 'biometrico', label: 'Biométrico', icon: Fingerprint, color: '#ec4899' },
    { key: 'conectividad', label: 'Conectividad', icon: Wifi, color: '#06b6d4' },
    { key: 'cuentas_seguridad', label: 'Cuentas y Seguridad', icon: Shield, color: '#f59e0b' },
    { key: 'email', label: 'Email', icon: Mail, color: '#3b82f6' },
    { key: 'fuerza_movil', label: 'Fuerza Móvil', icon: Smartphone, color: '#10b981' },
    { key: 'hardware', label: 'Hardware', icon: Monitor, color: '#64748b' },
    { key: 'impresora', label: 'Impresora', icon: Printer, color: '#8b5cf6' },
    { key: 'profit', label: 'Profit', icon: Database, color: '#0891b2' },
    { key: 'recursos_compartidos', label: 'Recursos Compartidos', icon: FolderOpen, color: '#d97706' },
    { key: 'software', label: 'Software', icon: Cpu, color: '#7c3aed' },
    { key: 'spi', label: 'SPI', icon: HardDrive, color: '#059669' },
    { key: 'telefonia', label: 'Telefonía y Comunicaciones', icon: Phone, color: '#dc2626' },
];

const ESTADOS = {
    abierto: { label: 'Abierto', icon: Circle, color: '#3b82f6', bg: '#eff6ff' },
    en_progreso: { label: 'En progreso', icon: CircleDot, color: '#f59e0b', bg: '#fffbeb' },
    cerrado: { label: 'Cerrado', icon: CircleCheck, color: '#10b981', bg: '#ecfdf5' },
};

const PRIORIDADES = {
    baja: { label: 'Baja', icon: ArrowDown, color: '#64748b', bg: '#f8fafc' },
    media: { label: 'Media', icon: Minus, color: '#3b82f6', bg: '#eff6ff' },
    alta: { label: 'Alta', icon: ArrowUp, color: '#f59e0b', bg: '#fffbeb' },
    critica: { label: 'Critica', icon: AlertTriangle, color: '#ef4444', bg: '#fef2f2' },
};

const getCatInfo = (key) => CATEGORIAS.find(c => c.key === key) || { key, label: key, icon: Monitor, color: '#64748b' };

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
const ALLOWED_EXTENSIONS = ['txt', 'pdf', 'png', 'jpg', 'jpeg', 'gif', 'doc', 'docx', 'xls', 'xlsx', 'zip', 'rar'];

const validateFiles = (files) => {
    const ok = [];
    for (const file of files) {
        if (file.size > MAX_FILE_SIZE) {
            alert(`${file.name}: excede el tamaño máximo permitido (20MB)`);
            continue;
        }
        const ext = file.name.split('.').pop()?.toLowerCase();
        if (!ALLOWED_EXTENSIONS.includes(ext)) {
            alert(`${file.name}: tipo no permitido. Extensiones permitidas: ${ALLOWED_EXTENSIONS.join(', ')}`);
            continue;
        }
        ok.push(file);
    }
    return ok;
};

// Base de conocimiento: solo imagenes, hasta 10MB cada una (mismo limite del backend)
const KB_MAX_IMAGE_SIZE = 10 * 1024 * 1024;
const KB_IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp'];
const KB_FORM_VACIO = { titulo: '', contenido: '', categoria: '', tags: '' };

// Editor del contenido: el mismo de Publicaciones (negritas, listas, titulos, colores...)
const KB_QUILL_MODULES = {
    toolbar: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'color': [] }, { 'background': [] }],
        ['link'],
        ['clean'],
    ],
};
const KB_QUILL_FORMATS = [
    'header', 'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'color', 'background', 'link',
];

// Los articulos viejos son texto simple; los nuevos, HTML del editor.
const kbEsHtml = (texto) => /<\/?[a-z][^>]*>/i.test(texto || '');

const kbTextoPlano = (texto) => {
    if (!kbEsHtml(texto)) return texto || '';
    const doc = new DOMParser().parseFromString(texto, 'text/html');
    return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
};

// Texto simple -> parrafos, para abrir un articulo viejo en el editor sin perder los saltos de linea
const kbTextoAHtml = (texto) => {
    if (!texto || kbEsHtml(texto)) return texto || '';
    const escapar = (t) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return texto.split('\n').map(linea => `<p>${linea.trim() ? escapar(linea) : '<br>'}</p>`).join('');
};

// Limpia el HTML antes de mostrarlo: sin scripts, iframes, eventos ni enlaces javascript:
const kbHtmlSeguro = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    doc.querySelectorAll('script, style, iframe, object, embed, link, meta, form').forEach(el => el.remove());
    doc.body.querySelectorAll('*').forEach(el => {
        [...el.attributes].forEach(attr => {
            const nombre = attr.name.toLowerCase();
            const valor = (attr.value || '').trim().toLowerCase();
            if (nombre.startsWith('on') || ((nombre === 'href' || nombre === 'src') && valor.startsWith('javascript:'))) {
                el.removeAttribute(attr.name);
            }
        });
        if (el.tagName === 'A') { el.setAttribute('target', '_blank'); el.setAttribute('rel', 'noreferrer'); }
    });
    return doc.body.innerHTML;
};

const kbContarPalabras = (texto) => kbTextoPlano(texto).split(/\s+/).filter(Boolean).length;

// Selector con la lista propia: a diferencia del <select> del navegador, siempre se despliega hacia abajo
const SelectHaciaAbajo = ({ value, onChange, options, placeholder }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const fuera = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        const escape = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', fuera);
        document.addEventListener('keydown', escape);
        return () => {
            document.removeEventListener('mousedown', fuera);
            document.removeEventListener('keydown', escape);
        };
    }, [open]);

    const todas = [{ value: '', label: placeholder }, ...options];
    const actual = todas.find(o => o.value === value) || todas[0];

    return (
        <div className="tk-dropdown" ref={ref}>
            <button type="button" className={`tk-select tk-dropdown-btn ${open ? 'open' : ''}`}
                onClick={() => setOpen(o => !o)} aria-haspopup="listbox" aria-expanded={open}>
                <span>{actual.label}</span>
                <ChevronDown size={16} />
            </button>
            {open && (
                <ul className="tk-dropdown-menu" role="listbox">
                    {todas.map(o => (
                        <li key={o.value || 'todas'}>
                            <button type="button" role="option" aria-selected={o.value === value}
                                className={o.value === value ? 'active' : ''}
                                onClick={() => { onChange(o.value); setOpen(false); }}>
                                <span>{o.label}</span>
                                {o.value === value && <Check size={14} />}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

const KbContenido = ({ contenido, className = '' }) => (
    kbEsHtml(contenido)
        ? <div className={`tk-kb-rich ${className}`} dangerouslySetInnerHTML={{ __html: kbHtmlSeguro(contenido) }} />
        : <div className={className}>{contenido}</div>
);

const validateKbImages = (files) => {
    const ok = [];
    for (const file of files) {
        const ext = file.name.split('.').pop()?.toLowerCase();
        if (!KB_IMAGE_EXTENSIONS.includes(ext)) {
            alert(`${file.name}: solo se permiten imagenes (${KB_IMAGE_EXTENSIONS.join(', ')})`);
            continue;
        }
        if (file.size > KB_MAX_IMAGE_SIZE) {
            alert(`${file.name}: excede el tamaño máximo permitido (10MB)`);
            continue;
        }
        ok.push(file);
    }
    return ok;
};

// ============================================================
// COMPONENTES AUXILIARES
// ============================================================

const StatusBadge = ({ status }) => {
    const s = ESTADOS[status] || ESTADOS.abierto;
    const Icon = s.icon;
    return (
        <span className="tk-status-badge" style={{ color: s.color, backgroundColor: s.bg }}>
            <Icon size={14} /> {s.label}
        </span>
    );
};

const CategoryBadge = ({ categoria }) => {
    const cat = getCatInfo(categoria);
    const Icon = cat.icon;
    return (
        <span className="tk-cat-badge" style={{ '--cat-color': cat.color }}>
            <Icon size={14} /> {cat.label}
        </span>
    );
};

const PriorityBadge = ({ prioridad }) => {
    const p = PRIORIDADES[prioridad] || PRIORIDADES.media;
    const Icon = p.icon;
    return (
        <span className="tk-priority-badge" style={{ color: p.color, backgroundColor: p.bg }}>
            <Icon size={13} /> {p.label}
        </span>
    );
};

const SlaTimer = ({ ticket }) => {
    const [now, setNow] = useState(new Date());
    useEffect(() => {
        if (ticket.status === 'cerrado' || ticket.frozen) return;
        const t = setInterval(() => setNow(new Date()), 60000);
        return () => clearInterval(t);
    }, [ticket.status, ticket.frozen]);

    if (!ticket.created_at || !ticket.sla_hours) return null;
    const created = new Date(ticket.created_at);
    const frozenMs = (ticket.frozen_total_seconds || 0) * 1000;

    if (ticket.status === 'cerrado' && ticket.closed_at) {
        const closed = new Date(ticket.closed_at);
        const effectiveMs = Math.max(0, (closed - created) - frozenMs);
        const h = (effectiveMs / 3600000).toFixed(1);
        const met = parseFloat(h) <= ticket.sla_hours;
        return (
            <span className={`tk-sla ${met ? 'green' : 'red'}`}>
                <Clock size={14} /> Resuelto en {h}h {met ? '' : `(SLA: ${ticket.sla_hours}h)`}
            </span>
        );
    }

    if (ticket.frozen) {
        return <span className="tk-sla frozen"><Clock size={14} /> SLA pausado</span>;
    }

    const deadlineMs = created.getTime() + ticket.sla_hours * 3600000 + frozenMs;
    const remainingMs = deadlineMs - now.getTime();
    if (remainingMs < 0) {
        const oh = Math.abs(remainingMs / 3600000).toFixed(1);
        return <span className="tk-sla red"><Clock size={14} /> Vencido hace {oh}h</span>;
    }
    const totalMs = ticket.sla_hours * 3600000;
    const pct = remainingMs / totalMs;
    const colorCls = pct < 0.25 ? 'red' : pct < 0.5 ? 'yellow' : 'green';
    const h = Math.floor(remainingMs / 3600000);
    const m = Math.floor((remainingMs % 3600000) / 60000);
    return <span className={`tk-sla ${colorCls}`}><Clock size={14} /> {h}h {m}m restantes</span>;
};

// ============================================================
// FUNCIONES AUXILIARES
// ============================================================

const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Ahora';
    if (diffMin < 60) return `Hace ${diffMin}m`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `Hace ${diffH}h`;
    const diffD = Math.floor(diffH / 24);
    if (diffD === 1) return 'Ayer';
    if (diffD < 7) return `Hace ${diffD}d`;
    return d.toLocaleDateString('es-VE', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatFullDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString('es-VE', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
    });
};

// ============================================================
// WATCHER PICKER (selector de invitados)
// ============================================================

const WatcherPicker = ({ selected, onAdd, onRemove, ticketId, placeholder = 'Buscar personas para copiar...' }) => {
    const [query, setQuery] = useState('');
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [dropdownRect, setDropdownRect] = useState(null);
    const wrapRef = useRef(null);
    const inputWrapRef = useRef(null);
    const dropdownRef = useRef(null);

    const loadOptions = useCallback(async () => {
        setLoading(true);
        try {
            const params = ticketId ? { ticket_id: ticketId } : {};
            const res = await axiosInstance.get('/api/tickets/eligible-watchers', { params });
            setOptions(res.data);
            setLoaded(true);
        } catch (e) { /* silent */ }
        finally { setLoading(false); }
    }, [ticketId]);

    const updatePosition = useCallback(() => {
        if (!inputWrapRef.current) return;
        const r = inputWrapRef.current.getBoundingClientRect();
        setDropdownRect({ top: r.bottom + 4, left: r.left, width: r.width });
    }, []);

    useLayoutEffect(() => {
        if (!open) return;
        updatePosition();
        const handleScroll = () => updatePosition();
        window.addEventListener('scroll', handleScroll, true);
        window.addEventListener('resize', updatePosition);
        return () => {
            window.removeEventListener('scroll', handleScroll, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [open, updatePosition]);

    useEffect(() => {
        const handler = (e) => {
            const insideWrap = wrapRef.current && wrapRef.current.contains(e.target);
            const insideDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
            if (!insideWrap && !insideDropdown) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const openPicker = () => {
        setOpen(true);
        if (!loaded) loadOptions();
    };

    const selectedIds = new Set(selected.map(s => s.user_id));
    const q = normalizarTexto(query).trim();
    const filtered = options
        .filter(o => !selectedIds.has(o.user_id))
        .filter(o => !q
            || normalizarTexto(o.nombre).includes(q)
            || normalizarTexto(o.ficha).includes(q)
            || normalizarTexto(o.area).includes(q)
            || normalizarTexto(o.cargo).includes(q))
        .slice(0, 50);

    const handlePick = (opt) => {
        onAdd(opt);
        setQuery('');
    };

    const dropdown = open && dropdownRect && createPortal(
        <div
            ref={dropdownRef}
            className="tk-watcher-dropdown"
            style={{ top: dropdownRect.top, left: dropdownRect.left, width: dropdownRect.width }}
        >
            {loading ? (
                <div className="tk-watcher-empty"><Loader2 size={14} className="tk-spin" /> Cargando...</div>
            ) : filtered.length === 0 ? (
                <div className="tk-watcher-empty">
                    {query ? 'Sin resultados' : 'No hay personas disponibles'}
                </div>
            ) : (
                filtered.map(o => (
                    <button
                        key={o.user_id}
                        type="button"
                        className="tk-watcher-option"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handlePick(o)}
                    >
                        <div className="tk-watcher-option-main">
                            <strong>{o.nombre}</strong>
                            {o.ficha && <span className="tk-watcher-ficha">#{o.ficha}</span>}
                        </div>
                        <div className="tk-watcher-option-sub">
                            {[o.cargo, o.area].filter(Boolean).join(' · ')}
                        </div>
                    </button>
                ))
            )}
        </div>,
        document.body
    );

    return (
        <div className="tk-watcher-picker" ref={wrapRef}>
            {selected.length > 0 && (
                <div className="tk-watcher-chips">
                    {selected.map(s => (
                        <div key={s.user_id} className="tk-watcher-chip">
                            <Users size={13} />
                            <span>{s.nombre}</span>
                            {onRemove && (
                                <button type="button" onClick={() => onRemove(s)} title="Quitar">
                                    <X size={13} />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}
            <div className="tk-watcher-input-wrap" ref={inputWrapRef}>
                <Search size={16} className="tk-watcher-icon" />
                <input
                    type="text"
                    placeholder={placeholder}
                    value={query}
                    onFocus={openPicker}
                    onChange={e => { setQuery(e.target.value); if (!open) openPicker(); }}
                />
            </div>
            {dropdown}
        </div>
    );
};

// ============================================================
// COMPONENTE PRINCIPAL
// ============================================================

const TicketsSoporte = () => {
    const { user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const authToken = localStorage.getItem('token');
    const isAdmin = user?.role === 'admin' || user?.role === 'ti';

    // Vistas y navegacion
    const [view, setView] = useState('list');
    const [adminTab, setAdminTab] = useState('tickets');
    const [selectedTicket, setSelectedTicket] = useState(null);

    // Lista de tickets
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('abierto');
    const [filterCat, setFilterCat] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [onlyMine, setOnlyMine] = useState(false);

    // Crear ticket
    const [form, setForm] = useState({ titulo: '', descripcion: '', categoria: '', prioridad: 'media', equipo: '' });
    const [formFiles, setFormFiles] = useState([]);
    const [creating, setCreating] = useState(false);
    const manualCat = useRef(false);

    // Detalle
    const [ticketDetail, setTicketDetail] = useState(null);
    const [convertModalOpen, setConvertModalOpen] = useState(false);
    const [relatedRefresh, setRelatedRefresh] = useState(0);
    const [copiedLink, setCopiedLink] = useState(false);
    const [mentionCandidates, setMentionCandidates] = useState([]);
    const [mentionQuery, setMentionQuery] = useState(null); // null = no estoy mencionando
    const [mentionIndex, setMentionIndex] = useState(0);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [comment, setComment] = useState('');
    const [commentFiles, setCommentFiles] = useState([]);
    const [sendingComment, setSendingComment] = useState(false);
    const sendingCommentRef = useRef(false);
    const [updatingStatus, setUpdatingStatus] = useState(false);
    const commentsEndRef = useRef(null);
    const fileInputRef = useRef(null);
    const commentFileRef = useRef(null);
    const commentInputRef = useRef(null);

    // IA
    const [aiClassification, setAiClassification] = useState(null);
    const [loadingAiClass, setLoadingAiClass] = useState(false);
    const [aiSuggestion, setAiSuggestion] = useState(null);
    const [loadingAiSuggest, setLoadingAiSuggest] = useState(false);
    const classifyTimer = useRef(null);

    // Base de conocimiento
    const [kbSuggestions, setKbSuggestions] = useState([]);
    const [allKbArticles, setAllKbArticles] = useState([]);
    const [kbForm, setKbForm] = useState(KB_FORM_VACIO);
    const [editingKb, setEditingKb] = useState(null);
    const kbTimer = useRef(null);
    const kbFileRef = useRef(null);
    const kbFormRef = useRef(null);
    const [kbFiles, setKbFiles] = useState([]);              // imagenes nuevas por subir
    const [kbImages, setKbImages] = useState([]);            // imagenes que ya tiene el articulo en edicion
    const [kbRemoveUuids, setKbRemoveUuids] = useState([]);  // imagenes que se quitan al guardar
    const [savingKb, setSavingKb] = useState(false);
    const [kbSearch, setKbSearch] = useState('');
    const [kbFilterCat, setKbFilterCat] = useState('');
    const [kbDetail, setKbDetail] = useState(null);          // articulo abierto en detalle
    const [kbViewerIndex, setKbViewerIndex] = useState(null);
    const [kbEditorOpen, setKbEditorOpen] = useState(false);  // ventana grande para escribir el contenido
    const [kbDraft, setKbDraft] = useState('');
    const [kbConfirmDescartar, setKbConfirmDescartar] = useState(false);
    const [kbDraftCambiado, setKbDraftCambiado] = useState(false); // solo cambios hechos por la persona

    // Respuestas predefinidas
    const [cannedResponses, setCannedResponses] = useState([]);
    const [showCannedMenu, setShowCannedMenu] = useState(false);
    const [cannedForm, setCannedForm] = useState({ titulo: '', contenido: '', categoria: '' });

    // Equipo del usuario
    const [userEquipment, setUserEquipment] = useState([]);

    // Invitados
    const [createWatchers, setCreateWatchers] = useState([]); // {user_id, nombre}
    const [addingWatcher, setAddingWatcher] = useState(false);
    const [showAddWatchers, setShowAddWatchers] = useState(false);
    const [pendingWatchers, setPendingWatchers] = useState([]);

    // Filtros que llegan del tablero de reportes (drill-down); viven en estado, no en la URL
    const [drillFilters, setDrillFilters] = useState(null);

    // Visor de archivos
    const [viewerFile, setViewerFile] = useState(null);

    const ticketFiles = useMemo(() => {
        const t = ticketDetail;
        if (!t) return [];
        const list = [];
        (t.attachments || []).forEach(a => {
            if (a.mime_type?.startsWith('image/')) {
                list.push({ file: a, senderName: t.creator_name, timestamp: formatDate(t.created_at) });
            }
        });
        (t.comments || []).forEach(c => {
            (c.attachments || []).forEach(a => {
                if (a.mime_type?.startsWith('image/')) {
                    list.push({ file: a, senderName: c.user_name, timestamp: formatDate(c.created_at) });
                }
            });
        });
        return list;
    }, [ticketDetail]);

    const openImage = useCallback((attachment, senderName, timestamp) => {
        const idx = ticketFiles.findIndex(f => f.file.uuid === attachment.uuid);
        setViewerFile({
            file: attachment,
            senderName,
            timestamp,
            index: idx >= 0 ? idx : 0,
        });
    }, [ticketFiles]);

    // ============================================================
    // EFECTOS Y CARGA DE DATOS
    // ============================================================

    const fetchTickets = useCallback(async () => {
        setLoading(true);
        try {
            const endpoint = isAdmin ? '/api/tickets/admin' : '/api/tickets';
            const params = {};
            if (isAdmin && drillFilters) {
                if (drillFilters.assigned_to) params.assigned_to = drillFilters.assigned_to;
                if (drillFilters.sin_asignar) params.sin_asignar = 1;
                if (drillFilters.area_id !== undefined && drillFilters.area_id !== null) params.area_id = drillFilters.area_id;
                if (drillFilters.desde) params.desde = drillFilters.desde;
                if (drillFilters.hasta) params.hasta = drillFilters.hasta;
                if (drillFilters.congelado) params.congelado = 1;
                if (drillFilters.sla_vencido) params.sla_vencido = 1;
                if (drillFilters.sla_por_vencer) params.sla_por_vencer = 1;
            }
            const res = await axiosInstance.get(endpoint, { params });
            setTickets(res.data);
        } catch (err) { console.error('Error cargando tickets:', err); }
        finally { setLoading(false); }
    }, [isAdmin, drillFilters]);

    useEffect(() => { fetchTickets(); }, [fetchTickets]);

    // Admin: cargar respuestas predefinidas
    useEffect(() => {
        if (isAdmin) {
            axiosInstance.get('/api/tickets/canned-responses')
                .then(r => setCannedResponses(r.data)).catch(() => {});
        }
    }, [isAdmin]);

    // KB management: cargar al cambiar tab
    useEffect(() => {
        if (adminTab === 'knowledge') {
            axiosInstance.get('/api/tickets/knowledge')
                .then(r => setAllKbArticles(r.data)).catch(() => {});
        }
    }, [adminTab]);

    // Equipo: cargar al abrir formulario
    useEffect(() => {
        if (view === 'create') {
            axiosInstance.get('/api/tickets/user-equipment')
                .then(r => setUserEquipment(r.data)).catch(() => {});
        }
    }, [view]);

    // IA: Clasificacion automatica (debounce)
    useEffect(() => {
        if (view !== 'create') return;
        if (!form.titulo || form.titulo.length < 5) {
            setAiClassification(null);
            return;
        }
        clearTimeout(classifyTimer.current);
        classifyTimer.current = setTimeout(async () => {
            setLoadingAiClass(true);
            try {
                const res = await axiosInstance.post('/api/tickets/ai/classify', {
                    titulo: form.titulo, descripcion: form.descripcion,
                });
                setAiClassification(res.data);
                // Siempre auto-rellenar categoria y prioridad
                if (res.data.categoria) {
                    setForm(f => ({ ...f, categoria: res.data.categoria, prioridad: res.data.prioridad || f.prioridad }));
                }
                // Auto-seleccionar equipo segun tipo
                const eqTipo = res.data.equipo_tipo;
                if (eqTipo && eqTipo !== 'none') {
                    const match = userEquipment.find(eq => eq.tipo === eqTipo);
                    if (match) {
                        setForm(f => ({ ...f, equipo: `${match.tipo}|${match.id}` }));
                    }
                } else {
                    setForm(f => ({ ...f, equipo: '' }));
                }
            } catch (e) {}
            setLoadingAiClass(false);
        }, 1500);
        return () => clearTimeout(classifyTimer.current);
    }, [form.titulo, view, userEquipment]); // eslint-disable-line react-hooks/exhaustive-deps

    // KB: Buscar articulos relevantes (debounce)
    useEffect(() => {
        if (view !== 'create') return;
        if (!form.titulo || form.titulo.length < 3) { setKbSuggestions([]); return; }
        clearTimeout(kbTimer.current);
        kbTimer.current = setTimeout(async () => {
            try {
                const res = await axiosInstance.get('/api/tickets/knowledge', { params: { q: form.titulo } });
                setKbSuggestions(res.data.slice(0, 3));
            } catch (e) {}
        }, 1000);
        return () => clearTimeout(kbTimer.current);
    }, [form.titulo, view]);

    // ============================================================
    // HANDLERS
    // ============================================================

    const openTicket = useCallback(async (ticketId) => {
        setView('detail');
        setLoadingDetail(true);
        try {
            const res = await axiosInstance.get(`/api/tickets/${ticketId}`);
            setTicketDetail(res.data);
            setSelectedTicket(ticketId);
            // Cargar candidatos a mencionar en comentarios
            try {
                const mc = await axiosInstance.get('/api/tickets/mention-candidates', { params: { ticket_id: ticketId } });
                setMentionCandidates(mc.data || []);
            } catch (_) { setMentionCandidates([]); }
        } catch (err) { console.error('Error cargando ticket:', err); }
        finally { setLoadingDetail(false); }
    }, []);

    // Renderizar contenido del comentario con menciones resaltadas
    const renderCommentContent = useCallback((content) => {
        if (!content) return null;
        if (!mentionCandidates.length) return content;
        const names = mentionCandidates
            .map(c => c.nombre)
            .filter(Boolean)
            .sort((a, b) => b.length - a.length);
        const escaped = names.map(n => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
        const pattern = new RegExp(`@(${escaped.join('|')})`, 'g');
        const out = [];
        let last = 0;
        let m;
        while ((m = pattern.exec(content)) !== null) {
            if (m.index > last) out.push(content.slice(last, m.index));
            out.push(<span key={`m-${m.index}`} className="tk-mention">@{m[1]}</span>);
            last = m.index + m[0].length;
        }
        if (last < content.length) out.push(content.slice(last));
        return out;
    }, [mentionCandidates]);

    // Insertar mención en el textarea de comentario
    const insertMentionTk = useCallback((cand) => {
        const ta = commentInputRef.current;
        if (!ta) return;
        const pos = ta.selectionStart;
        const before = comment.substring(0, pos);
        const after = comment.substring(pos);
        const at = before.lastIndexOf('@');
        if (at < 0) return;
        const newText = before.substring(0, at) + `@${cand.nombre} ` + after;
        setComment(newText);
        setMentionQuery(null);
        setTimeout(() => {
            const newPos = at + cand.nombre.length + 2;
            ta.setSelectionRange(newPos, newPos);
            ta.focus();
        }, 0);
    }, [comment]);

    // URL = single source of truth: si hay ?ticket=<id> abrir el detalle, si no volver a lista.
    // Permite compartir links, refresh y back/forward del navegador.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const ticketId = params.get('ticket');
        if (ticketId) {
            const id = parseInt(ticketId, 10);
            if (selectedTicket !== id) openTicket(id);
        } else if (view === 'detail') {
            setView('list');
            setTicketDetail(null);
            setSelectedTicket(null);
            setShowCannedMenu(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    const scrollToLastComment = () => {
        if (commentsEndRef.current) commentsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    };

    // Auto-resize del textarea de comentario
    useLayoutEffect(() => {
        const el = commentInputRef.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
    }, [comment]);

    const hasUrgenteInTitle = /urgente/i.test(form.titulo);

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!form.titulo.trim() || !form.descripcion.trim() || !form.categoria) return;
        if (hasUrgenteInTitle) return;
        setCreating(true);
        try {
            const fd = new FormData();
            fd.append('titulo', form.titulo);
            fd.append('descripcion', form.descripcion);
            fd.append('categoria', form.categoria);
            fd.append('prioridad', form.prioridad);
            if (form.equipo) {
                const [tipo, id] = form.equipo.split('|');
                fd.append('equipo_tipo', tipo);
                fd.append('equipo_id', id);
            }
            formFiles.forEach(f => fd.append('files', f));
            if (createWatchers.length > 0) {
                fd.append('watcher_ids', JSON.stringify(createWatchers.map(w => w.user_id)));
            }
            await axiosInstance.post('/api/tickets', fd);
            setForm({ titulo: '', descripcion: '', categoria: '', prioridad: 'media', equipo: '' });
            setFormFiles([]);
            setCreateWatchers([]);
            setAiClassification(null);
            setAiSuggestion(null);
            setKbSuggestions([]);
            manualCat.current = false;
            setView('list');
            fetchTickets();
        } catch (err) { console.error('Error creando ticket:', err); }
        finally { setCreating(false); }
    };

    const handleAddWatchersSubmit = async () => {
        if (pendingWatchers.length === 0) return;
        setAddingWatcher(true);
        try {
            const res = await axiosInstance.post(`/api/tickets/${selectedTicket}/watchers`, {
                user_ids: pendingWatchers.map(w => w.user_id),
            });
            setTicketDetail(prev => prev ? { ...prev, watchers: res.data.watchers } : prev);
            setPendingWatchers([]);
            setShowAddWatchers(false);
        } catch (err) { console.error('Error agregando invitados:', err); }
        finally { setAddingWatcher(false); }
    };

    const handleRemoveWatcher = async (userId) => {
        try {
            await axiosInstance.delete(`/api/tickets/${selectedTicket}/watchers/${userId}`);
            setTicketDetail(prev => prev ? {
                ...prev,
                watchers: (prev.watchers || []).filter(w => w.user_id !== userId),
            } : prev);
        } catch (err) { console.error('Error quitando invitado:', err); }
    };

    // Pegar archivos/imagenes desde el portapapeles (Ctrl+V / Cmd+V)
    const handleCommentPaste = (e) => {
        const items = e.clipboardData?.items;
        if (!items) return;
        const pastedFiles = [];
        for (const item of items) {
            if (item.kind === 'file') {
                const file = item.getAsFile();
                if (!file) continue;
                e.preventDefault();
                let fileName = file.name;
                if (!fileName || fileName === 'image.png') {
                    const ext = file.type.split('/')[1] || 'png';
                    fileName = `imagen_pegada_${Date.now()}_${pastedFiles.length}.${ext}`;
                }
                pastedFiles.push(new window.File([file], fileName, { type: file.type }));
            }
        }
        if (pastedFiles.length > 0) {
            const valid = validateFiles(pastedFiles);
            if (valid.length > 0) setCommentFiles(prev => [...prev, ...valid]);
        }
    };

    const handleComment = async () => {
        if (sendingCommentRef.current) return;
        if (!comment.trim() && commentFiles.length === 0) return;
        sendingCommentRef.current = true;
        setSendingComment(true);
        try {
            const fd = new FormData();
            fd.append('content', comment);
            commentFiles.forEach(f => fd.append('files', f));
            await axiosInstance.post(`/api/tickets/${selectedTicket}/comments`, fd);
            setComment('');
            setCommentFiles([]);
            const res = await axiosInstance.get(`/api/tickets/${selectedTicket}`);
            setTicketDetail(res.data);
            if (isAdmin) fetchTickets();
            setTimeout(scrollToLastComment, 100);
        } catch (err) { console.error('Error enviando comentario:', err); }
        finally { sendingCommentRef.current = false; setSendingComment(false); }
    };

    const handleStatusChange = async (newStatus) => {
        setUpdatingStatus(true);
        try {
            await axiosInstance.put(`/api/tickets/${selectedTicket}/status`, { status: newStatus });
            const res = await axiosInstance.get(`/api/tickets/${selectedTicket}`);
            setTicketDetail(res.data);
            fetchTickets();
        } catch (err) { console.error('Error cambiando estado:', err); }
        finally { setUpdatingStatus(false); }
    };

    const handleAssign = async () => {
        try {
            await axiosInstance.put(`/api/tickets/${selectedTicket}/assign`);
            const res = await axiosInstance.get(`/api/tickets/${selectedTicket}`);
            setTicketDetail(res.data);
            fetchTickets();
        } catch (err) { console.error('Error asignando ticket:', err); }
    };

    const handleRelease = async () => {
        try {
            await axiosInstance.put(`/api/tickets/${selectedTicket}/release`);
            const res = await axiosInstance.get(`/api/tickets/${selectedTicket}`);
            setTicketDetail(res.data);
            fetchTickets();
        } catch (err) { console.error('Error soltando ticket:', err); }
    };

    const handleToggleFreeze = async () => {
        setUpdatingStatus(true);
        try {
            await axiosInstance.put(`/api/tickets/${selectedTicket}/freeze`);
            const res = await axiosInstance.get(`/api/tickets/${selectedTicket}`);
            setTicketDetail(res.data);
            fetchTickets();
        } catch (err) { console.error('Error cambiando congelacion:', err); }
        finally { setUpdatingStatus(false); }
    };

    const handleAiSuggest = async () => {
        setLoadingAiSuggest(true);
        try {
            const res = await axiosInstance.post('/api/tickets/ai/suggest', {
                titulo: form.titulo, descripcion: form.descripcion, categoria: form.categoria,
            });
            setAiSuggestion(res.data);
        } catch (e) {}
        setLoadingAiSuggest(false);
    };

    const handleUseCanned = (content) => {
        setComment(content);
        setShowCannedMenu(false);
    };

    // KB CRUD
    const resetKbForm = () => {
        setKbForm(KB_FORM_VACIO);
        setEditingKb(null);
        setKbFiles([]);
        setKbImages([]);
        setKbRemoveUuids([]);
    };

    const startEditKb = (art) => {
        setEditingKb(art.id);
        setKbForm({ titulo: art.titulo, contenido: kbTextoAHtml(art.contenido), categoria: art.categoria || '', tags: art.tags?.join(', ') || '' });
        setKbFiles([]);
        setKbImages(art.images || []);
        setKbRemoveUuids([]);
    };

    const kbContenidoVacio = !kbTextoPlano(kbForm.contenido).trim();

    const abrirEditorKb = () => {
        setKbDraft(kbForm.contenido);
        setKbDraftCambiado(false);
        setKbConfirmDescartar(false);
        setKbEditorOpen(true);
    };

    const guardarEditorKb = () => {
        setKbForm(f => ({ ...f, contenido: kbDraft }));
        setKbConfirmDescartar(false);
        setKbEditorOpen(false);
    };

    // Cerrar sin guardar: si hay cambios, primero se pregunta
    const cerrarEditorKb = () => {
        if (kbDraftCambiado && !kbConfirmDescartar) {
            setKbConfirmDescartar(true);
            return;
        }
        setKbConfirmDescartar(false);
        setKbEditorOpen(false);
    };

    const handleSaveKb = async () => {
        if (!kbForm.titulo || kbContenidoVacio || savingKb) return;
        setSavingKb(true);
        try {
            const fd = new FormData();
            Object.entries(kbForm).forEach(([k, v]) => fd.append(k, v));
            kbFiles.forEach(f => fd.append('images', f));
            if (editingKb) {
                fd.append('remove_image_uuids', JSON.stringify(kbRemoveUuids));
                await axiosInstance.put(`/api/tickets/knowledge/${editingKb}`, fd);
            } else {
                await axiosInstance.post('/api/tickets/knowledge', fd);
            }
            resetKbForm();
            const res = await axiosInstance.get('/api/tickets/knowledge');
            setAllKbArticles(res.data);
        } catch (e) {
            alert(e.response?.data?.error || 'No se pudo guardar el articulo');
        } finally {
            setSavingKb(false);
        }
    };

    const handleDeleteKb = async (id) => {
        try {
            await axiosInstance.delete(`/api/tickets/knowledge/${id}`);
            setAllKbArticles(prev => prev.filter(a => a.id !== id));
            if (editingKb === id) resetKbForm();
        } catch (e) {}
    };

    // Canned CRUD
    const handleSaveCanned = async () => {
        if (!cannedForm.titulo || !cannedForm.contenido) return;
        try {
            await axiosInstance.post('/api/tickets/canned-responses', cannedForm);
            setCannedForm({ titulo: '', contenido: '', categoria: '' });
            const res = await axiosInstance.get('/api/tickets/canned-responses');
            setCannedResponses(res.data);
        } catch (e) {}
    };

    const handleDeleteCanned = async (id) => {
        try {
            await axiosInstance.delete(`/api/tickets/canned-responses/${id}`);
            setCannedResponses(prev => prev.filter(r => r.id !== id));
        } catch (e) {}
    };

    // Drill-down desde el tablero de reportes
    const handleDrill = (f) => {
        if (f.ticket) { navigate(`?ticket=${f.ticket}`); return; }
        const { status, categoria, ...rest } = f;
        const hasRest = Object.keys(rest).length > 0;
        setDrillFilters(hasRest ? rest : null);
        setFilterCat(categoria || '');
        // Con periodo o filtros de SLA la lista debe mostrar todos los estados
        setFilterStatus(status || (hasRest ? '' : 'abierto'));
        setOnlyMine(false);
        setSearchQuery('');
        setAdminTab('tickets');
    };

    const removeDrillKey = (keys) => {
        setDrillFilters(prev => {
            if (!prev) return prev;
            const next = { ...prev };
            keys.forEach(k => { delete next[k]; });
            return Object.keys(next).length ? next : null;
        });
    };

    const drillChips = [];
    if (drillFilters) {
        if (drillFilters.assigned_to) drillChips.push({ label: `Técnico: ${drillFilters.assignee_name || drillFilters.assigned_to}`, keys: ['assigned_to', 'assignee_name'] });
        if (drillFilters.sin_asignar) drillChips.push({ label: 'Sin asignar', keys: ['sin_asignar'] });
        if (drillFilters.area_id !== undefined && drillFilters.area_id !== null) drillChips.push({ label: `Área: ${drillFilters.area_name || drillFilters.area_id}`, keys: ['area_id', 'area_name'] });
        if (drillFilters.desde || drillFilters.hasta) drillChips.push({ label: `Del ${drillFilters.desde || '…'} al ${drillFilters.hasta || '…'}`, keys: ['desde', 'hasta'] });
        if (drillFilters.congelado) drillChips.push({ label: 'Congelados', keys: ['congelado'] });
        if (drillFilters.sla_vencido) drillChips.push({ label: 'SLA vencido', keys: ['sla_vencido'] });
        if (drillFilters.sla_por_vencer) drillChips.push({ label: 'Por vencer SLA', keys: ['sla_por_vencer'] });
    }

    // Filtrado
    const scopedTickets = (isAdmin && onlyMine)
        ? tickets.filter(t => t.assigned_to === user?.id)
        : tickets;

    const filteredTickets = scopedTickets.filter(t => {
        if (filterStatus && t.status !== filterStatus) return false;
        if (filterCat && t.categoria !== filterCat) return false;
        if (searchQuery) {
            const q = normalizarTexto(searchQuery);
            if (!normalizarTexto(t.titulo).includes(q) &&
                !normalizarTexto(t.creator_name).includes(q) &&
                !`#${t.id}`.includes(q)) return false;
        }
        return true;
    });

    const counts = {
        total: scopedTickets.length,
        abierto: scopedTickets.filter(t => t.status === 'abierto').length,
        en_progreso: scopedTickets.filter(t => t.status === 'en_progreso').length,
        cerrado: scopedTickets.filter(t => t.status === 'cerrado').length,
    };

    // ============================================================
    // VISTA: CREAR TICKET
    // ============================================================

    if (view === 'create') {
        return (
            <div className="tk-page">
                <motion.div className="tk-container" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                    <button className="tk-back" onClick={() => { setView('list'); setAiClassification(null); setAiSuggestion(null); setKbSuggestions([]); setCreateWatchers([]); manualCat.current = false; }}>
                        <ArrowLeft size={20} /> <span>Volver</span>
                    </button>

                    <div className="tk-create-card">
                        <div className="tk-create-header">
                            <h2>Nuevo Ticket</h2>
                            <p>Describe tu problema y el equipo de TI se encargara</p>
                        </div>

                        <form onSubmit={handleCreate}>
                            {/* Titulo */}
                            <div className="tk-field">
                                <label>Que necesitas?</label>
                                <input
                                    type="text"
                                    placeholder="Ej: No puedo conectarme a la VPN..."
                                    value={form.titulo}
                                    onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))}
                                    maxLength={200}
                                    required
                                    autoFocus
                                />
                                {loadingAiClass && !hasUrgenteInTitle && (
                                    <div className="tk-ai-classifying">
                                        <Loader2 size={15} className="tk-spin" /> Analizando tu solicitud...
                                    </div>
                                )}
                                {hasUrgenteInTitle && (
                                    <div className="tk-title-warning">
                                        <AlertCircle size={15} />
                                        <span>Evita usar la palabra <strong>"urgente"</strong> en el titulo. La prioridad se asigna automaticamente segun el contenido. Describe el problema concretamente.</span>
                                    </div>
                                )}
                            </div>

                            {/* Clasificacion IA (aparece cuando IA responde) */}
                            {aiClassification && !loadingAiClass && (
                                <motion.div className="tk-ai-classification" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
                                    <div className="tk-ai-class-header"><Wand2 size={14} /> Clasificado automaticamente</div>
                                    <div className="tk-ai-class-badges">
                                        <CategoryBadge categoria={form.categoria} />
                                        <PriorityBadge prioridad={form.prioridad} />
                                        {form.equipo && (
                                            <span className="tk-ai-equipo-badge">
                                                <Monitor size={13} />
                                                {userEquipment.find(eq => `${eq.tipo}|${eq.id}` === form.equipo)?.label || 'Equipo'}
                                            </span>
                                        )}
                                    </div>
                                </motion.div>
                            )}

                            {/* KB Sugerencias */}
                            {kbSuggestions.length > 0 && (
                                <div className="tk-kb-suggestions">
                                    <div className="tk-kb-header">
                                        <BookOpen size={16} />
                                        <span>Articulos que pueden ayudarte</span>
                                    </div>
                                    {kbSuggestions.map(art => (
                                        <details key={art.id} className="tk-kb-card">
                                            <summary>
                                                <span>{art.titulo}</span>
                                                {art.categoria && <CategoryBadge categoria={art.categoria} />}
                                            </summary>
                                            <KbContenido contenido={art.contenido} className="tk-kb-content" />
                                        </details>
                                    ))}
                                </div>
                            )}

                            {/* Descripcion */}
                            <div className="tk-field">
                                <label>Descripcion</label>
                                <textarea
                                    placeholder="Explica con detalle que sucede, cuando ocurre, que equipo afecta..."
                                    value={form.descripcion}
                                    onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))}
                                    rows={5}
                                    required
                                />
                            </div>

                            {/* Adjuntos */}
                            <div className="tk-field">
                                <label>Adjuntos <span className="tk-optional">(opcional)</span></label>
                                <div className="tk-dropzone" onClick={() => fileInputRef.current?.click()}>
                                    <Paperclip size={22} />
                                    <span>Haz click para adjuntar capturas o archivos</span>
                                    <input ref={fileInputRef} type="file" multiple hidden
                                        onChange={e => setFormFiles(prev => [...prev, ...Array.from(e.target.files)])} />
                                </div>
                                {formFiles.length > 0 && (
                                    <div className="tk-chips">
                                        {formFiles.map((f, i) => (
                                            <div key={i} className="tk-chip">
                                                <Paperclip size={13} /> <span>{f.name}</span>
                                                <button type="button" onClick={() => setFormFiles(prev => prev.filter((_, j) => j !== i))}><X size={14} /></button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Invitados (copiar personas) */}
                            <div className="tk-field">
                                <label>
                                    <Users size={15} style={{ display: 'inline', verticalAlign: '-2px', marginRight: 4 }} />
                                    Copiar a <span className="tk-optional">(opcional)</span>
                                </label>
                                <p className="tk-field-hint">Las personas que agregues podran ver el ticket, recibir notificaciones y comentar.</p>
                                <WatcherPicker
                                    selected={createWatchers}
                                    onAdd={(opt) => setCreateWatchers(prev => [...prev, opt])}
                                    onRemove={(opt) => setCreateWatchers(prev => prev.filter(p => p.user_id !== opt.user_id))}
                                />
                            </div>

                            {/* IA Sugerencia */}
                            {form.titulo.length >= 5 && form.descripcion.length >= 10 && !aiSuggestion && (
                                <button type="button" className="tk-ai-suggest-btn" onClick={handleAiSuggest} disabled={loadingAiSuggest}>
                                    {loadingAiSuggest
                                        ? <><Loader2 size={16} className="tk-spin" /> Analizando...</>
                                        : <><Wand2 size={16} /> Obtener sugerencia antes de enviar</>}
                                </button>
                            )}
                            {aiSuggestion && (
                                <motion.div className="tk-ai-panel" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                                    <div className="tk-ai-panel-header"><Wand2 size={16} /> Sugerencia</div>
                                    <p>{aiSuggestion.sugerencia}</p>
                                    <span className="tk-ai-hint">Si esto no resuelve tu problema, envia el ticket igualmente</span>
                                </motion.div>
                            )}

                            <button type="submit" className="tk-primary-btn" disabled={creating || !form.titulo || !form.descripcion || !form.categoria || hasUrgenteInTitle}>
                                {creating ? 'Enviando...' : 'Enviar Ticket'} <Send size={18} />
                            </button>
                        </form>
                    </div>
                </motion.div>
            </div>
        );
    }

    // ============================================================
    // VISTA: DETALLE
    // ============================================================

    if (view === 'detail') {
        const t = ticketDetail;
        return (
            <div className="tk-page">
                <motion.div className="tk-container" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                    <button className="tk-back" onClick={() => navigate(location.pathname)}>
                        <ArrowLeft size={20} /> <span>Volver</span>
                    </button>

                    {loadingDetail ? (
                        <div className="tk-loading">Cargando ticket...</div>
                    ) : t ? (
                        <div className="tk-detail-card">
                            {/* Header */}
                            <div className="tk-detail-top">
                                <div className="tk-detail-info">
                                    <div className="tk-detail-badges">
                                        <span className="tk-ticket-num">#{t.id}</span>
                                        <button
                                            type="button"
                                            className="tk-share-btn"
                                            title={copiedLink ? '¡Link copiado!' : 'Copiar link al ticket'}
                                            onClick={() => {
                                                const url = `${window.location.origin}/soporte-ti?ticket=${t.id}`;
                                                navigator.clipboard?.writeText(url).then(() => {
                                                    setCopiedLink(true);
                                                    setTimeout(() => setCopiedLink(false), 2000);
                                                });
                                            }}
                                        >
                                            {copiedLink ? <><Check size={13} /> Link copiado</> : <><Link2 size={13} /> Copiar link</>}
                                        </button>
                                        <button
                                            type="button"
                                            className="tk-share-btn"
                                            title="Convertir este ticket en una tarea del módulo Proyectos"
                                            onClick={() => setConvertModalOpen(true)}
                                        >
                                            <KanbanSquare size={13} /> Escalar a tarea
                                        </button>
                                        <StatusBadge status={t.status} />
                                        {t.frozen && (
                                            <span className="tk-frozen-badge">
                                                <Snowflake size={13} /> Congelado
                                            </span>
                                        )}
                                        <CategoryBadge categoria={t.categoria} />
                                        <PriorityBadge prioridad={t.prioridad} />
                                        <SlaTimer ticket={t} />
                                    </div>
                                    <h2>{t.titulo}</h2>
                                    <div className="tk-detail-byline">
                                        <span>{t.creator_name}</span>
                                        {t.creator_area && <><span className="tk-dot">&middot;</span><span>{t.creator_area}</span></>}
                                        <span className="tk-dot">&middot;</span>
                                        <span>{formatFullDate(t.created_at)}</span>
                                    </div>
                                    {t.assignee_name && (
                                        <div className="tk-assigned-to">
                                            <UserCheck size={15} /> Asignado a <strong>{t.assignee_name}</strong>
                                        </div>
                                    )}
                                    {t.equipo && (
                                        <div className="tk-equipo-info">
                                            <Monitor size={15} /> Equipo: <strong>{t.equipo.label}</strong>
                                            {t.equipo.serial && <span className="tk-equipo-serial">({t.equipo.serial})</span>}
                                        </div>
                                    )}
                                    {t.first_response_at && (
                                        <div className="tk-first-response">
                                            <Clock size={14} /> Primera respuesta: {formatFullDate(t.first_response_at)}
                                        </div>
                                    )}
                                </div>

                                {/* Admin actions */}
                                {isAdmin && (
                                    <div className="tk-detail-actions">
                                        {t.assigned_to !== user?.id && t.status !== 'cerrado' && (
                                            <button
                                                className="tk-act-btn teal"
                                                onClick={handleAssign}
                                                title={t.assigned_to ? `Lo tiene ${t.assignee_name}; pasa a tu nombre` : undefined}
                                            >
                                                <UserCheck size={16} /> Tomar
                                            </button>
                                        )}
                                        {t.assigned_to === user?.id && t.status !== 'cerrado' && (
                                            <button className="tk-act-btn slate" onClick={handleRelease} title="Queda sin asignar para que otro lo tome">
                                                <UserMinus size={16} /> Soltar
                                            </button>
                                        )}
                                        {t.status !== 'cerrado' && (
                                            <button
                                                className={`tk-act-btn ${t.frozen ? 'cyan' : 'slate'}`}
                                                onClick={handleToggleFreeze}
                                                disabled={updatingStatus}
                                                title={t.frozen ? 'Reanudar: vuelve a contar el SLA' : 'Congelar: pausa el SLA mientras el ticket depende de factores externos'}
                                            >
                                                {t.frozen ? <><Play size={16} /> Descongelar</> : <><Snowflake size={16} /> Congelar</>}
                                            </button>
                                        )}
                                        {t.status !== 'cerrado' && (
                                            <button className="tk-act-btn green" onClick={() => handleStatusChange('cerrado')} disabled={updatingStatus}>
                                                <CircleCheck size={16} /> Cerrar
                                            </button>
                                        )}
                                        {t.status === 'cerrado' && (
                                            <button className="tk-act-btn blue" onClick={() => handleStatusChange('abierto')} disabled={updatingStatus}>
                                                <RotateCcw size={16} /> Reabrir
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Tareas vinculadas del modulo Proyectos */}
                            <div style={{ padding: '0 24px', marginBottom: 16 }}>
                                <TareasRelacionadasPanel
                                    key={`ticket-${t.id}-${relatedRefresh}`}
                                    entidadTipo="ticket"
                                    entidadId={t.id}
                                />
                            </div>

                            {/* Invitados */}
                            <div className="tk-watchers-section">
                                <div className="tk-watchers-header">
                                    <span className="tk-watchers-title">
                                        <Users size={15} /> Invitados {t.watchers?.length > 0 && <em>({t.watchers.length})</em>}
                                    </span>
                                    {t.status !== 'cerrado' && (
                                        <button
                                            type="button"
                                            className="tk-watchers-add-btn"
                                            onClick={() => { setShowAddWatchers(v => !v); setPendingWatchers([]); }}
                                        >
                                            <UserPlus size={14} /> {showAddWatchers ? 'Cancelar' : 'Agregar invitado'}
                                        </button>
                                    )}
                                </div>

                                {t.watchers?.length > 0 ? (
                                    <div className="tk-watcher-chips readonly">
                                        {t.watchers.map(w => {
                                            const canRemove = isAdmin
                                                || t.created_by === user?.id
                                                || w.user_id === user?.id;
                                            return (
                                                <div key={w.user_id} className="tk-watcher-chip">
                                                    <Users size={13} />
                                                    <span>{w.user_name}</span>
                                                    {canRemove && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveWatcher(w.user_id)}
                                                            title={w.user_id === user?.id ? 'Salir del ticket' : 'Quitar invitado'}
                                                        >
                                                            <X size={13} />
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    !showAddWatchers && <p className="tk-watchers-empty">Sin invitados. Puedes copiar personas para que vean y comenten el ticket.</p>
                                )}

                                {showAddWatchers && (
                                    <div className="tk-watchers-add-panel">
                                        <WatcherPicker
                                            selected={pendingWatchers}
                                            onAdd={(opt) => setPendingWatchers(prev => [...prev, opt])}
                                            onRemove={(opt) => setPendingWatchers(prev => prev.filter(p => p.user_id !== opt.user_id))}
                                            ticketId={t.id}
                                            placeholder="Buscar persona para invitar..."
                                        />
                                        <div className="tk-watchers-add-actions">
                                            <button
                                                type="button"
                                                className="tk-primary-btn small"
                                                disabled={addingWatcher || pendingWatchers.length === 0}
                                                onClick={handleAddWatchersSubmit}
                                            >
                                                {addingWatcher ? 'Agregando...' : 'Invitar'}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Descripcion */}
                            <div className="tk-detail-desc">
                                <p>{t.descripcion}</p>
                                {t.attachments?.length > 0 && (
                                    <div className="tk-detail-files">
                                        {t.attachments.map(a => {
                                            const isImage = a.mime_type?.startsWith('image/');
                                            const url = `${a.download_url}?token=${authToken}`;
                                            return isImage ? (
                                                <button
                                                    key={a.id}
                                                    type="button"
                                                    className="tk-inline-img-btn"
                                                    onClick={() => openImage(a, t.creator_name, formatDate(t.created_at))}
                                                >
                                                    <img src={url} alt={a.original_name} className="tk-inline-img" />
                                                </button>
                                            ) : (
                                                <a key={a.id} href={url} className="tk-file-link" target="_blank" rel="noreferrer">
                                                    <Download size={15} /> {a.original_name}
                                                </a>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* Comentarios */}
                            <div className="tk-comments">
                                <h3><MessageSquare size={18} /> Conversacion ({t.comments?.length || 0})</h3>

                                <div className="tk-thread">
                                    {t.comments?.length === 0 && <p className="tk-no-comments">Aun no hay comentarios</p>}
                                    {t.comments?.map((c, i) => (
                                        <motion.div key={c.id} className={`tk-msg ${(c.user_role === 'admin' || c.user_role === 'ti') ? 'support' : ''}`}
                                            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                                            <div className="tk-msg-head">
                                                <strong>{c.user_name}</strong>
                                                {(c.user_role === 'admin' || c.user_role === 'ti') && <span className="tk-support-tag">Soporte TI</span>}
                                                <span className="tk-msg-time">{formatDate(c.created_at)}</span>
                                            </div>
                                            <p>{renderCommentContent(c.content)}</p>
                                            {c.attachments?.length > 0 && (
                                                <div className="tk-detail-files">
                                                    {c.attachments.map(a => {
                                                        const isImage = a.mime_type?.startsWith('image/');
                                                        const url = `${a.download_url}?token=${authToken}`;
                                                        return isImage ? (
                                                            <button
                                                                key={a.id}
                                                                type="button"
                                                                className="tk-inline-img-btn"
                                                                onClick={() => openImage(a, c.user_name, formatDate(c.created_at))}
                                                            >
                                                                <img src={url} alt={a.original_name} className="tk-inline-img" />
                                                            </button>
                                                        ) : (
                                                            <a key={a.id} href={url} className="tk-file-link" target="_blank" rel="noreferrer">
                                                                <Download size={14} /> {a.original_name}
                                                            </a>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </motion.div>
                                    ))}
                                    <div ref={commentsEndRef} />
                                </div>

                                {t.status !== 'cerrado' && (
                                    <div className="tk-reply">
                                        {commentFiles.length > 0 && (
                                            <div className="tk-chips compact">
                                                {commentFiles.map((f, i) => (
                                                    <div key={i} className="tk-chip">
                                                        <Paperclip size={13} /> <span>{f.name}</span>
                                                        <button onClick={() => setCommentFiles(prev => prev.filter((_, j) => j !== i))}><X size={14} /></button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        <div className="tk-reply-bar">
                                            <button className="tk-reply-attach" onClick={() => commentFileRef.current?.click()}>
                                                <Paperclip size={19} />
                                                <input ref={commentFileRef} type="file" multiple hidden
                                                    onChange={e => {
                                                        const valid = validateFiles(Array.from(e.target.files));
                                                        if (valid.length > 0) setCommentFiles(prev => [...prev, ...valid]);
                                                        e.target.value = '';
                                                    }} />
                                            </button>

                                            {/* Respuestas rapidas (admin) */}
                                            {isAdmin && cannedResponses.length > 0 && (
                                                <div className="tk-canned-wrapper">
                                                    <button className="tk-reply-canned" onClick={() => setShowCannedMenu(!showCannedMenu)} title="Respuestas rapidas">
                                                        <Zap size={18} />
                                                    </button>
                                                    {showCannedMenu && (
                                                        <div className="tk-canned-menu">
                                                            <div className="tk-canned-title">Respuestas rapidas</div>
                                                            {cannedResponses
                                                                .filter(cr => !cr.categoria || cr.categoria === t.categoria)
                                                                .map(cr => (
                                                                    <button key={cr.id} className="tk-canned-item" onClick={() => handleUseCanned(cr.contenido)}>
                                                                        <strong>{cr.titulo}</strong>
                                                                        <span>{cr.contenido.slice(0, 60)}...</span>
                                                                    </button>
                                                                ))
                                                            }
                                                            {cannedResponses.filter(cr => !cr.categoria || cr.categoria === t.categoria).length === 0 && (
                                                                <p className="tk-canned-empty">No hay respuestas para esta categoria</p>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {mentionQuery !== null && (() => {
                                                const filtered = mentionCandidates
                                                    .filter(c => incluyeTexto(c.nombre, mentionQuery))
                                                    .slice(0, 6);
                                                if (filtered.length === 0) return null;
                                                return (
                                                    <div className="tk-mention-dropdown">
                                                        {filtered.map((c, idx) => (
                                                            <button
                                                                key={c.user_id}
                                                                type="button"
                                                                className={`tk-mention-item ${idx === mentionIndex ? 'active' : ''}`}
                                                                onMouseDown={(e) => { e.preventDefault(); insertMentionTk(c); }}
                                                            >
                                                                <span className="tk-mention-name">{c.nombre}</span>
                                                                {c.cargo && <span className="tk-mention-cargo">{c.cargo}</span>}
                                                                {c.role === 'admin' || c.role === 'ti' ? <span className="tk-mention-role">TI</span> : null}
                                                            </button>
                                                        ))}
                                                    </div>
                                                );
                                            })()}
                                            <textarea
                                                ref={commentInputRef}
                                                className="tk-reply-input"
                                                placeholder="Escribe un comentario... (usa @ para mencionar)"
                                                value={comment}
                                                rows={1}
                                                onChange={e => {
                                                    const val = e.target.value;
                                                    setComment(val);
                                                    const cursorPos = e.target.selectionStart;
                                                    const before = val.substring(0, cursorPos);
                                                    const m = before.match(/@([\w\sáéíóúñÁÉÍÓÚÑ]*)$/);
                                                    if (m) {
                                                        setMentionQuery(m[1] || '');
                                                        setMentionIndex(0);
                                                    } else {
                                                        setMentionQuery(null);
                                                    }
                                                }}
                                                onPaste={handleCommentPaste}
                                                onKeyDown={e => {
                                                    if (mentionQuery !== null) {
                                                        const filtered = mentionCandidates
                                                            .filter(c => incluyeTexto(c.nombre, mentionQuery))
                                                            .slice(0, 6);
                                                        if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIndex(prev => Math.min(prev + 1, filtered.length - 1)); return; }
                                                        if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIndex(prev => Math.max(prev - 1, 0)); return; }
                                                        if ((e.key === 'Enter' || e.key === 'Tab') && filtered[mentionIndex]) {
                                                            e.preventDefault();
                                                            insertMentionTk(filtered[mentionIndex]);
                                                            return;
                                                        }
                                                        if (e.key === 'Escape') { setMentionQuery(null); return; }
                                                    }
                                                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleComment(); }
                                                }}
                                            />
                                            <button className="tk-reply-send" onClick={handleComment}
                                                disabled={sendingComment || (!comment.trim() && commentFiles.length === 0)}>
                                                <Send size={18} />
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : null}
                </motion.div>

                {viewerFile && (
                    <FileViewer
                        file={viewerFile.file}
                        senderName={viewerFile.senderName}
                        timestamp={viewerFile.timestamp}
                        baseUrl={BASE_URL}
                        allFiles={ticketFiles}
                        currentIndex={viewerFile.index}
                        onClose={() => setViewerFile(null)}
                    />
                )}

                <CrearTareaModal
                    isOpen={convertModalOpen}
                    onClose={() => setConvertModalOpen(false)}
                    tituloPrefill={t?.titulo || ''}
                    descripcionPrefill={t?.descripcion || ''}
                    prioridadPrefill={
                        t?.prioridad === 'alta' || t?.prioridad === 'urgente' ? t.prioridad : 'media'
                    }
                    entidadTipo="ticket"
                    entidadId={t?.id}
                    contextLabel={t ? `Desde ticket #${t.id}: ${t.titulo}` : null}
                    onCreated={() => setRelatedRefresh((v) => v + 1)}
                    navigateAfter={false}
                />
            </div>
        );
    }

    // ============================================================
    // VISTA: LISTA (con tabs admin)
    // ============================================================

    const kbQuery = kbSearch.trim();
    const filteredKbArticles = allKbArticles.filter(a =>
        (!kbFilterCat || a.categoria === kbFilterCat)
        && (!kbQuery
            || incluyeTexto(a.titulo, kbQuery)
            || incluyeTexto(kbTextoPlano(a.contenido), kbQuery)
            || (a.tags || []).some(t => incluyeTexto(t, kbQuery))));

    const kbDetailFiles = (kbDetail?.images || []).map(img => ({
        file: img,
        senderName: kbDetail.creator_name,
        timestamp: formatDate(kbDetail.updated_at || kbDetail.created_at),
    }));

    const renderKnowledgeManage = () => (
        <div className="tk-admin-section">
            <div className="tk-admin-form" ref={kbFormRef}>
                <h4>{editingKb ? 'Editar articulo' : 'Nuevo articulo'}</h4>
                <input type="text" placeholder="Titulo del articulo" value={kbForm.titulo}
                    onChange={e => setKbForm(f => ({ ...f, titulo: e.target.value }))} />
                {/* Contenido: vista previa; al hacer click se abre el editor grande */}
                <button type="button" className={`tk-kb-content-field ${kbContenidoVacio ? 'vacio' : ''}`}
                    onClick={abrirEditorKb}>
                    <span className="tk-kb-content-head">
                        <span className="tk-kb-content-label">Contenido</span>
                        <span className="tk-kb-content-hint">
                            <Maximize2 size={14} /> {kbContenidoVacio ? 'Escribir contenido' : 'Editar contenido'}
                        </span>
                    </span>
                    {kbContenidoVacio ? (
                        <span className="tk-kb-content-placeholder">
                            Instrucciones, pasos o la solucion.
                        </span>
                    ) : (
                        <span className="tk-kb-content-placeholder">
                            Contenido escrito · {kbContarPalabras(kbForm.contenido)} palabras. Haz click para verlo o editarlo.
                        </span>
                    )}
                </button>
                <div className="tk-admin-form-row">
                    <select value={kbForm.categoria} onChange={e => setKbForm(f => ({ ...f, categoria: e.target.value }))}>
                        <option value="">Todas las categorias</option>
                        {CATEGORIAS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                    </select>
                    <input type="text" placeholder="Tags (separados por coma)" value={kbForm.tags}
                        onChange={e => setKbForm(f => ({ ...f, tags: e.target.value }))} />
                </div>

                {/* Imagenes del articulo */}
                <div className="tk-dropzone" onClick={() => kbFileRef.current?.click()}>
                    <Paperclip size={22} />
                    <span>Haz click para adjuntar imagenes</span>
                    <input ref={kbFileRef} type="file" accept="image/*" multiple hidden
                        onChange={e => {
                            const valid = validateKbImages(Array.from(e.target.files));
                            setKbFiles(prev => [...prev, ...valid]);
                            e.target.value = '';
                        }} />
                </div>
                {kbImages.length > 0 && (
                    <div className="tk-detail-files">
                        {kbImages.map(img => (
                            <div key={img.uuid} className="tk-kb-thumb">
                                <img src={`${img.download_url}?token=${authToken}`} alt={img.original_name} className="tk-inline-img" />
                                <button type="button" title="Quitar imagen"
                                    onClick={() => {
                                        setKbImages(prev => prev.filter(i => i.uuid !== img.uuid));
                                        setKbRemoveUuids(prev => [...prev, img.uuid]);
                                    }}>
                                    <X size={13} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
                {kbFiles.length > 0 && (
                    <div className="tk-chips">
                        {kbFiles.map((f, i) => (
                            <div key={i} className="tk-chip">
                                <Paperclip size={13} /> <span>{f.name}</span>
                                <button type="button" onClick={() => setKbFiles(prev => prev.filter((_, j) => j !== i))}><X size={14} /></button>
                            </div>
                        ))}
                    </div>
                )}

                <div className="tk-admin-form-actions">
                    <button className="tk-primary-btn small" onClick={handleSaveKb} disabled={!kbForm.titulo || kbContenidoVacio || savingKb}>
                        {savingKb ? <Loader2 size={16} className="tk-spin" /> : <Save size={16} />} {editingKb ? 'Actualizar' : 'Guardar'}
                    </button>
                    {editingKb && (
                        <button className="tk-back small" onClick={resetKbForm}>
                            Cancelar
                        </button>
                    )}
                </div>
            </div>

            {allKbArticles.length > 0 && (
                <div className="tk-toolbar">
                    <div className="tk-searchbox">
                        <Search size={18} />
                        <input type="text" placeholder="Buscar por titulo, contenido o etiquetas..."
                            value={kbSearch} onChange={e => setKbSearch(e.target.value)} />
                    </div>
                    <SelectHaciaAbajo
                        value={kbFilterCat}
                        onChange={setKbFilterCat}
                        placeholder="Todas las categorias"
                        options={CATEGORIAS.map(c => ({ value: c.key, label: c.label }))}
                    />
                </div>
            )}

            <div className="tk-admin-list">
                {allKbArticles.length === 0 && <p className="tk-no-comments">No hay articulos aun</p>}
                {allKbArticles.length > 0 && filteredKbArticles.length === 0 && (
                    <p className="tk-no-comments">Ningun articulo coincide con la busqueda o la categoria</p>
                )}
                {filteredKbArticles.map(art => (
                    <div key={art.id} className="tk-admin-item clickable" onClick={() => setKbDetail(art)}>
                        <div className="tk-admin-item-info">
                            <strong>{art.titulo}</strong>
                            <span className="tk-admin-item-meta">
                                {art.categoria && <CategoryBadge categoria={art.categoria} />}
                                {art.tags?.length > 0 && <span className="tk-admin-tags">{art.tags.join(', ')}</span>}
                            </span>
                            <p className="tk-admin-item-preview">{kbTextoPlano(art.contenido).slice(0, 120)}...</p>
                        </div>
                        <div className="tk-admin-item-actions" onClick={e => e.stopPropagation()}>
                            <button title="Ver articulo" onClick={() => setKbDetail(art)}>
                                <Eye size={15} />
                            </button>
                            <button title="Editar" onClick={() => startEditKb(art)}>
                                <Wand2 size={15} />
                            </button>
                            <button title="Eliminar" onClick={() => handleDeleteKb(art.id)}><Trash2 size={15} /></button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Editor grande del contenido */}
            <Modal isOpen={kbEditorOpen} onClose={cerrarEditorKb} size="2xl" closeOnEscape={false} className="tk-kb-editor-modal">
                <Modal.Header onClose={cerrarEditorKb}>
                    <span className="tk-kb-editor-title">
                        Contenido del articulo
                        {kbForm.titulo && <span className="tk-kb-editor-subtitle">{kbForm.titulo}</span>}
                    </span>
                </Modal.Header>
                <Modal.Body>
                    <ReactQuill
                        theme="snow"
                        className="tk-kb-editor tk-kb-editor-doc"
                        value={kbDraft}
                        onChange={(html, delta, source) => {
                            setKbDraft(html);
                            // Quill tambien avisa cuando reacomoda el texto al abrir; eso no cuenta como cambio
                            if (source === 'user') { setKbDraftCambiado(true); setKbConfirmDescartar(false); }
                        }}
                        modules={KB_QUILL_MODULES}
                        formats={KB_QUILL_FORMATS}
                        placeholder="Escribe las instrucciones, los pasos o la solucion."
                    />
                </Modal.Body>
                <Modal.Footer>
                    {kbConfirmDescartar ? (
                        <div className="tk-kb-editor-footer">
                            <span className="tk-kb-editor-warning">
                                <AlertTriangle size={15} /> Tienes cambios sin guardar. ¿Descartarlos?
                            </span>
                            <div className="tk-admin-form-actions">
                                <button className="tk-primary-btn small tk-danger-btn" onClick={cerrarEditorKb}>
                                    <Trash2 size={16} /> Descartar
                                </button>
                                <button className="tk-back small" onClick={() => setKbConfirmDescartar(false)}>
                                    Seguir editando
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="tk-kb-editor-footer">
                            <span className="tk-kb-content-count">{kbContarPalabras(kbDraft)} palabras</span>
                            <div className="tk-admin-form-actions">
                                <button className="tk-back small" onClick={cerrarEditorKb}>
                                    Cancelar
                                </button>
                                <button className="tk-primary-btn small" onClick={guardarEditorKb}>
                                    <Check size={16} /> Listo
                                </button>
                            </div>
                        </div>
                    )}
                </Modal.Footer>
            </Modal>

            {/* Detalle del articulo */}
            <Modal isOpen={!!kbDetail} onClose={() => setKbDetail(null)} size="xl">
                <Modal.Header onClose={() => setKbDetail(null)}>{kbDetail?.titulo}</Modal.Header>
                <Modal.Body>
                    {kbDetail && (
                        <div className="tk-kb-detail">
                            {(kbDetail.categoria || kbDetail.tags?.length > 0) && (
                                <span className="tk-admin-item-meta">
                                    {kbDetail.categoria && <CategoryBadge categoria={kbDetail.categoria} />}
                                    {kbDetail.tags?.length > 0 && <span className="tk-admin-tags">{kbDetail.tags.join(', ')}</span>}
                                </span>
                            )}
                            <KbContenido contenido={kbDetail.contenido} className="tk-kb-detail-content" />
                            {kbDetail.images?.length > 0 && (
                                <div className="tk-detail-files">
                                    {kbDetail.images.map((img, i) => (
                                        <button key={img.uuid} type="button" className="tk-inline-img-btn" onClick={() => setKbViewerIndex(i)}>
                                            <img src={`${img.download_url}?token=${authToken}`} alt={img.original_name} className="tk-inline-img" />
                                        </button>
                                    ))}
                                </div>
                            )}
                            {(kbDetail.creator_name || kbDetail.updated_at) && (
                                <span className="tk-admin-tags">
                                    {[kbDetail.creator_name, formatDate(kbDetail.updated_at || kbDetail.created_at)].filter(Boolean).join(' · ')}
                                </span>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <div className="tk-admin-form-actions">
                        <button className="tk-primary-btn small" onClick={() => {
                            startEditKb(kbDetail);
                            setKbDetail(null);
                            kbFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }}>
                            <Wand2 size={16} /> Editar
                        </button>
                        <button className="tk-back small" onClick={() => setKbDetail(null)}>
                            Cerrar
                        </button>
                    </div>
                </Modal.Footer>
            </Modal>

            {kbDetail && kbViewerIndex !== null && kbDetailFiles[kbViewerIndex] && (
                <FileViewer
                    file={kbDetailFiles[kbViewerIndex].file}
                    senderName={kbDetailFiles[kbViewerIndex].senderName}
                    timestamp={kbDetailFiles[kbViewerIndex].timestamp}
                    baseUrl={BASE_URL}
                    allFiles={kbDetailFiles}
                    currentIndex={kbViewerIndex}
                    onClose={() => setKbViewerIndex(null)}
                />
            )}
        </div>
    );

    const renderCannedManage = () => (
        <div className="tk-admin-section">
            <div className="tk-admin-form">
                <h4>Nueva respuesta rapida</h4>
                <input type="text" placeholder="Titulo (ej: Reiniciar VPN)" value={cannedForm.titulo}
                    onChange={e => setCannedForm(f => ({ ...f, titulo: e.target.value }))} />
                <textarea placeholder="Contenido de la respuesta..." value={cannedForm.contenido}
                    onChange={e => setCannedForm(f => ({ ...f, contenido: e.target.value }))} rows={3} />
                <select value={cannedForm.categoria} onChange={e => setCannedForm(f => ({ ...f, categoria: e.target.value }))}>
                    <option value="">Todas las categorias</option>
                    {CATEGORIAS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
                <button className="tk-primary-btn small" onClick={handleSaveCanned} disabled={!cannedForm.titulo || !cannedForm.contenido}>
                    <Save size={16} /> Guardar
                </button>
            </div>

            <div className="tk-admin-list">
                {cannedResponses.length === 0 && <p className="tk-no-comments">No hay respuestas aun</p>}
                {cannedResponses.map(cr => (
                    <div key={cr.id} className="tk-admin-item">
                        <div className="tk-admin-item-info">
                            <strong>{cr.titulo}</strong>
                            {cr.categoria && <CategoryBadge categoria={cr.categoria} />}
                            <p className="tk-admin-item-preview">{cr.contenido.slice(0, 100)}</p>
                        </div>
                        <div className="tk-admin-item-actions">
                            <button onClick={() => handleDeleteCanned(cr.id)}><Trash2 size={15} /></button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    return (
        <div className="tk-page">
            <motion.div className="tk-container" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                {/* Header */}
                <div className="tk-page-header">
                    <h1>Tickets</h1>
                    <button className="tk-primary-btn" onClick={() => setView('create')}>
                        <Plus size={20} /> Nuevo Ticket
                    </button>
                </div>

                {/* Admin Tabs */}
                {isAdmin && (
                    <div className="tk-tabs">
                        <button className={`tk-tab ${adminTab === 'tickets' ? 'active' : ''}`} onClick={() => setAdminTab('tickets')}>
                            <MessageSquare size={16} /> Tickets
                        </button>
                        <button className={`tk-tab ${adminTab === 'reports' ? 'active' : ''}`} onClick={() => setAdminTab('reports')}>
                            <BarChart3 size={16} /> Reportes
                        </button>
                        <button className={`tk-tab ${adminTab === 'knowledge' ? 'active' : ''}`} onClick={() => setAdminTab('knowledge')}>
                            <BookOpen size={16} /> Base de Conocimiento
                        </button>
                        <button className={`tk-tab ${adminTab === 'canned' ? 'active' : ''}`} onClick={() => setAdminTab('canned')}>
                            <Zap size={16} /> Respuestas Rapidas
                        </button>
                    </div>
                )}

                {/* Tab: Tickets */}
                {(adminTab === 'tickets' || !isAdmin) && (
                    <>
                        {/* Stats */}
                        <div className="tk-stats">
                            {Object.entries(ESTADOS).map(([key, val]) => {
                                const Icon = val.icon;
                                const isActive = filterStatus === key;
                                return (
                                    <button key={key} className={`tk-stat ${isActive ? 'active' : ''}`}
                                        onClick={() => { if (!isActive) setFilterStatus(key); }}>
                                        <div className="tk-stat-icon" style={{ backgroundColor: val.bg, color: val.color }}>
                                            <Icon size={20} />
                                        </div>
                                        <div className="tk-stat-info">
                                            <span className="tk-stat-num">{counts[key]}</span>
                                            <span className="tk-stat-label">{val.label}</span>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Search + filter */}
                        <div className="tk-toolbar">
                            <div className="tk-searchbox">
                                <Search size={18} />
                                <input type="text" placeholder="Buscar por titulo, #id o nombre..."
                                    value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
                            </div>
                            <select className="tk-select" value={filterCat} onChange={e => setFilterCat(e.target.value)}>
                                <option value="">Todas las categorias</option>
                                {CATEGORIAS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                            </select>
                            {isAdmin && (
                                <label className={`tk-toggle ${onlyMine ? 'active' : ''}`}>
                                    <input type="checkbox" checked={onlyMine} onChange={e => setOnlyMine(e.target.checked)} />
                                    <UserCheck size={16} />
                                    <span>Mis tickets</span>
                                </label>
                            )}
                        </div>

                        {/* Filtros del tablero de reportes */}
                        {isAdmin && drillChips.length > 0 && (
                            <div className="tk-drill-chips">
                                <span className="tk-drill-chips-label">Filtros del tablero</span>
                                {drillChips.map(ch => (
                                    <button key={ch.label} className="tk-drill-chip" onClick={() => removeDrillKey(ch.keys)} title="Quitar filtro">
                                        {ch.label} <X size={13} />
                                    </button>
                                ))}
                                <button className="tk-drill-clear" onClick={() => handleDrill({})}>Limpiar</button>
                            </div>
                        )}

                        {/* List */}
                        {loading ? (
                            <div className="tk-loading">Cargando tickets...</div>
                        ) : filteredTickets.length === 0 ? (
                            <div className="tk-empty">
                                <div className="tk-empty-icon"><Search size={40} strokeWidth={1.2} /></div>
                                <h3>No hay tickets</h3>
                                <p>{isAdmin ? 'No se encontraron tickets con estos filtros' : 'Crea uno nuevo si necesitas ayuda del equipo de TI'}</p>
                            </div>
                        ) : (
                            <div className="tk-list">
                                <AnimatePresence>
                                    {filteredTickets.map((t, i) => {
                                        const cat = getCatInfo(t.categoria);
                                        const CatIcon = cat.icon;
                                        const prio = PRIORIDADES[t.prioridad] || PRIORIDADES.media;
                                        return (
                                            <motion.div key={t.id} className="tk-row" onClick={() => navigate(`?ticket=${t.id}`)}
                                                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                                                <div className="tk-row-cat" style={{ backgroundColor: cat.color + '14', color: cat.color }}>
                                                    <CatIcon size={22} />
                                                </div>
                                                <div className="tk-row-content">
                                                    <div className="tk-row-top">
                                                        <span className="tk-row-id">#{t.id}</span>
                                                        <StatusBadge status={t.status} />
                                                        {t.frozen && (
                                                            <span className="tk-frozen-badge">
                                                                <Snowflake size={12} /> Congelado
                                                            </span>
                                                        )}
                                                        <PriorityBadge prioridad={t.prioridad} />
                                                        {!isAdmin && t.viewer_role === 'watcher' && (
                                                            <span className="tk-invited-badge" title="Fuiste invitado a este ticket">
                                                                <Eye size={12} /> Invitado
                                                            </span>
                                                        )}
                                                    </div>
                                                    <h4>{t.titulo}</h4>
                                                    <div className="tk-row-meta">
                                                        <span>{t.creator_name}</span>
                                                        {t.creator_area && <><span className="tk-dot">&middot;</span><span>{t.creator_area}</span></>}
                                                        <span className="tk-dot">&middot;</span>
                                                        <span>{formatDate(t.created_at)}</span>
                                                        {t.comment_count > 0 && (
                                                            <span className="tk-row-comments"><MessageSquare size={13} /> {t.comment_count}</span>
                                                        )}
                                                        {t.equipo && (
                                                            <span className="tk-row-equipo"><Monitor size={13} /> {t.equipo.label}</span>
                                                        )}
                                                    </div>
                                                </div>
                                                <ChevronRight size={20} className="tk-row-arrow" />
                                            </motion.div>
                                        );
                                    })}
                                </AnimatePresence>
                            </div>
                        )}
                    </>
                )}

                {/* Tab: Reportes */}
                {adminTab === 'reports' && isAdmin && <TicketsReportes categorias={CATEGORIAS} onDrill={handleDrill} />}

                {/* Tab: Base de Conocimiento */}
                {adminTab === 'knowledge' && isAdmin && renderKnowledgeManage()}

                {/* Tab: Respuestas Rapidas */}
                {adminTab === 'canned' && isAdmin && renderCannedManage()}
            </motion.div>
        </div>
    );
};

export default TicketsSoporte;
